import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Camera, Image, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CameraView from "./scan/CameraView";
import ImageUpload from "./scan/ImageUpload";
import ImagePreview from "./scan/ImagePreview";
import ScanHistory from "./scan/ScanHistory";
import NoMatchProduct from "./scan/NoMatchProduct";
import { useProductScanner } from "@/hooks/use-product-scanner";
import { Product } from "@/types/product";

interface ScanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ScanModal = ({ open, onOpenChange }: ScanModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [scanResults, setScanResults] = useState<{
    detectedText?: string;
    products?: Product[];
    scanId?: string;
    productInfo?: {
      brand?: string;
      productName?: string;
      volume?: string;
      ingredients?: string[];
    };
  } | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const { scanImage, isLoading } = useProductScanner();

  const startCamera = async () => {
    try {
      console.log('Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      console.log('Camera access granted:', {
        tracks: stream.getTracks().length,
        settings: stream.getTracks()[0].getSettings()
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsCameraActive(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast({
        title: t("errors.cameraError"),
        description: t("errors.pleaseAllowCamera"),
        variant: "destructive",
      });
    }
  };

  const stopCamera = () => {
    console.log('Stopping camera stream...');
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('Track stopped:', track.label);
      });
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  const handleImageSelect = (file: File) => {
    console.log('Selected image:', {
      name: file.name,
      type: file.type,
      size: file.size
    });
    setSelectedImage(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleCapture = (blob: Blob) => {
    console.log('Processing captured image:', {
      type: blob.type,
      size: blob.size
    });
    
    const file = new File([blob], "capture.jpg", { 
      type: "image/jpeg",
      lastModified: Date.now()
    });
    handleImageSelect(file);
    stopCamera();
  };

  const handleDirectAdd = () => {
    if (!selectedImage) {
      console.error('No image selected for adding product');
      return;
    }

    console.log('Navigating to new product form with image:', {
      imageName: selectedImage.name,
      imageSize: selectedImage.size,
      imageType: selectedImage.type
    });

    navigate('/product/new', { 
      state: { 
        imageFile: selectedImage,
        scanId: scanResults?.scanId,
        detectedText: scanResults?.detectedText,
        productInfo: scanResults?.productInfo
      }
    });
    onOpenChange(false);
  };

  const handleScan = async () => {
    if (!selectedImage) {
      console.error('No image selected for scanning');
      return;
    }
    
    try {
      setIsScanning(true);
      console.log('Starting scan process with file:', selectedImage);
      const result = await scanImage(selectedImage);
      console.log('Scan completed successfully:', result);
      setScanResults(result || {});

      // Show success message if products were found
      if (result?.products && result.products.length > 0) {
        toast({
          title: t("products.productFound"),
          description: t("products.matchesFound", { count: result.products.length }),
        });
      } else if (result?.detectedText) {
        toast({
          title: t("products.scanComplete"),
          description: t("products.noProductFound"),
        });
      }
    } catch (error) {
      console.error('Error during scan process:', error);
      toast({
        title: t("errors.scanError"),
        description: t("errors.pleaseTryAgain"),
        variant: "destructive",
      });
    } finally {
      setIsScanning(false);
    }
  };

  const resetSelection = () => {
    console.log('Resetting image selection and camera...');
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedImage(null);
    setPreviewUrl(null);
    setScanResults(null);
    stopCamera();
  };

  const handleClose = () => {
    resetSelection();
    onOpenChange(false);
  };

  useEffect(() => {
    return () => {
      resetSelection();
    };
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="sm:max-w-md p-0 overflow-hidden border-none rounded-xl bg-white shadow-xl transition-all"
        aria-labelledby="scan-modal-title"
        aria-describedby="scan-modal-description"
      >
        {!previewUrl && !isCameraActive && !scanResults ? (
          <div className="flex flex-col p-6 space-y-6">
            <div className="text-center space-y-2">
              <h2 id="scan-modal-title" className="text-xl font-semibold text-amber-600">
                {t("products.scanProduct")}
              </h2>
              <p id="scan-modal-description" className="text-sm text-gray-500">
                {t("products.scanInstructions")}
              </p>
            </div>
            
            <div className="flex flex-col gap-4">
              <Button 
                onClick={startCamera}
                className="w-full h-12 flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
              >
                <Camera className="w-5 h-5" />
                {t("products.useCamera")}
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    {t("common.or")}
                  </span>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full h-12 flex items-center justify-center gap-2 border-amber-200 hover:bg-amber-50 text-amber-800 rounded-lg transition-colors"
                onClick={() => document.getElementById('image-upload')?.click()}
              >
                <Image className="w-5 h-5" />
                {t("products.chooseImage")}
                <ImageUpload onImageSelect={handleImageSelect} />
              </Button>
            </div>
            
            <ScanHistory 
              className="mt-6" 
              onSelect={(scan) => {
                if (scan.product_id) {
                  navigate(`/product/${scan.product_id}`);
                  onOpenChange(false);
                }
              }} 
            />
          </div>
        ) : isCameraActive ? (
          <CameraView 
            videoRef={videoRef}
            onCapture={handleCapture} 
            onCancel={stopCamera} 
          />
        ) : scanResults ? (
          <div className="max-h-[80vh] overflow-y-auto">
            {scanResults.products && scanResults.products.length > 0 ? (
              <div className="p-6 space-y-4">
                <div className="flex items-center mb-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="mr-2" 
                    onClick={resetSelection}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <h3 className="text-lg font-semibold text-amber-600">
                    {t("products.matchingProducts")}
                  </h3>
                </div>
                
                <div className="grid gap-3">
                  {scanResults.products.map((product) => (
                    <Button
                      key={product.id}
                      variant="outline"
                      className="w-full text-left flex items-center gap-3 h-auto py-3 px-4 rounded-lg border-amber-200 hover:bg-amber-50 transition-colors"
                      onClick={() => {
                        navigate(`/product/${product.id}`);
                        onOpenChange(false);
                      }}
                    >
                      <img
                        src={product.image_url || "/placeholder.svg"}
                        alt={product.name}
                        className="w-14 h-14 object-cover rounded-md flex-shrink-0"
                      />
                      <div className="flex-1 overflow-hidden">
                        <p className="font-medium text-gray-900 truncate">{product.name}</p>
                        <p className="text-sm text-gray-500 truncate">
                          {product.brand}
                        </p>
                        {product.rating > 0 && (
                          <div className="flex items-center mt-1">
                            <div className="flex items-center">
                              <span className="text-amber-500">★</span>
                              <span className="text-xs ml-1 text-gray-700">{product.rating.toFixed(1)}</span>
                            </div>
                            <span className="text-xs ml-2 text-gray-500">
                              ({product.reviews_count || 0})
                            </span>
                          </div>
                        )}
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <NoMatchProduct
                detectedText={scanResults.detectedText}
                productInfo={scanResults.productInfo}
                onClose={handleClose}
                onScanAgain={resetSelection}
              />
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <ImagePreview imageUrl={previewUrl!} onReset={resetSelection} />
            
            <div className="flex flex-col gap-3 p-5">
              <Button 
                onClick={handleScan} 
                className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white rounded-lg shadow-sm transition-colors" 
                disabled={isLoading || isScanning}
              >
                {isLoading || isScanning ? (
                  <div className="flex items-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {t("common.scanning")}
                  </div>
                ) : t("products.scanProduct")}
              </Button>
              
              <Button
                onClick={handleDirectAdd}
                variant="outline"
                className="w-full h-12 border-amber-200 hover:bg-amber-50 text-amber-800 rounded-lg transition-colors"
                disabled={isLoading || isScanning}
              >
                {t("products.addDirectly")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScanModal;
